/**
 * 书架推荐 Tab。对照 ops2.jsx：排序 / 书编码+语言 / 书名 / 推荐语 / 下架过滤 / 上下架 / 操作。
 * 写动作走 PUT/DELETE。全部 = 全局层（app_id 空）；空表仍给「新增推荐书籍」。
 */

import type { ShelfRecommendationResponse } from '@novel-admin/shared';
import { type BookRow, useBooks } from '~/data/queries';
import {
  type ShelfRecommendationMutations,
  useShelfRecommendations,
} from '~/data/use-ops-shelf-popup';
import { langName } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { DISCOVERY_REACH_LABEL, discoveryReach } from '~/lib/novel-visibility-display';
import { ContextMenu } from '~/ui/ContextMenu';
import { Section } from '~/ui/Section';
import { Table } from '~/ui/Table';
import type { OpsScopeKind } from './ops-scope';
import { errorToast } from './use-ops-mutations';

const SHELF_MAX = 10;
const FORBIDDEN_ROLE_TITLE = '权限不足 · 该操作受角色限制（后端 requireRole 为权威）';

export function ShelfRecommendationsTab({
  appId,
  appName,
  lang,
  scope,
  mutations,
  canUpsert,
  canDelete,
  onEdit,
  onConfirm,
}: {
  appId: string;
  appName: string;
  lang: string;
  scope: OpsScopeKind;
  mutations: ShelfRecommendationMutations;
  canUpsert: boolean;
  canDelete: boolean;
  onEdit: (rec?: ShelfRecommendationResponse, existingCount?: number) => void;
  onConfirm: (cfg: {
    title: string;
    message: string;
    tone?: 'danger' | 'warn' | 'default';
    confirmLabel?: string;
    onConfirm: () => void;
  }) => void;
}) {
  const { data: recs } = useShelfRecommendations(appId, lang);
  const { data: books } = useBooks();
  const byNovelId = new Map<string, BookRow>();
  for (const b of books) {
    for (const loc of b.languageCodes) {
      if (loc.novelId) byNovelId.set(loc.novelId, b);
    }
    if (b.displayNovelId) byNovelId.set(b.displayNovelId, b);
  }
  const rows = recs;
  const full = rows.length >= SHELF_MAX;
  const bookOf = (novelId: string): Partial<BookRow> => byNovelId.get(novelId) ?? {};

  const move = (id: string, dir: -1 | 1) => {
    const i = rows.findIndex((r) => r.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= rows.length) return;
    const next = [...rows];
    const a = next[i];
    const b = next[j];
    if (!a || !b) return;
    // PATCH（§8.3）只带 sort_order：app_id / novel_id 契约明列不可改（前者定所属层、后者定
    // 配置对象与逻辑语言段），language 也不是本端点入参 —— 旧全字段 PUT 把它们全回传，
    // 并发下会按本地快照覆盖别人刚改的 is_listed。
    const onErr = (err: unknown) => errorToast(err, '保存排序失败');
    mutations.update.mutate({ id: a.id, body: { sort_order: b.sort_order } }, { onError: onErr });
    mutations.update.mutate({ id: b.id, body: { sort_order: a.sort_order } }, { onError: onErr });
  };

  const toggleListed = (r: ShelfRecommendationResponse) => {
    // 只翻 is_listed。PATCH 是 sparse 的，故不再需要从 cache 取实时行来防
    // 「stale 全 PUT 恢复 sort_order 旧值」—— 没传的键服务端不动。
    mutations.update.mutate(
      { id: r.id, body: { is_listed: !r.is_listed } },
      {
        onError: (err) => errorToast(err, r.is_listed ? '下架失败' : '上架失败'),
      },
    );
  };

  const emptyHint =
    scope === 'NO_OVERRIDE'
      ? '无覆盖 · 不继承全局书单。复制覆盖后才能加书'
      : scope === 'GLOBAL'
        ? '该语言全局暂无书 · 点右上角「新增推荐书籍」（选品不能翻译，不从英语抄）'
        : '覆盖当前为空（0 本，允许为空）· 空则接口不返回';

  return (
    <Section
      pad="4px 18px 16px"
      title={`书架推荐书籍 · ${lang.toUpperCase()} ${langName(lang)}`}
      sub={`上限 ${SHELF_MAX}、可以 0 · 位置即排序 · 推荐语写回该书行的 novels.recommendation_text_i18n[${lang}]`}
      action={
        <div className="hstack" style={{ gap: 6 }}>
          <span className={`chip ${full ? 'chip-warn' : ''} mono`} style={{ fontSize: 10 }}>
            {rows.length}/{SHELF_MAX}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-accent"
            disabled={!canUpsert || full}
            title={
              !canUpsert ? FORBIDDEN_ROLE_TITLE : full ? `已达上限 ${SHELF_MAX} 本` : undefined
            }
            onClick={() => canUpsert && onEdit(undefined, recs.length)}
          >
            <Icons.IconPlus size={11} />
            新增推荐书籍
          </button>
        </div>
      }
    >
      <Table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 50 }}>排序</th>
            <th style={{ width: 170 }}>书编码 + 语言</th>
            <th>书名</th>
            <th>推荐语（{lang.toUpperCase()}）</th>
            <th style={{ width: 150 }}>发现面触达</th>
            <th style={{ width: 72 }}>上下架</th>
            <th style={{ width: 92 }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const b = bookOf(r.novel_id);
            const title = r.novel_title ?? b.displayTitle ?? b.originalTitle ?? '（书名未回传）';
            const text = (r.recommendation_text_i18n?.[lang] ?? '').trim();
            // 书架推荐 serving 是发现面选行路径 → 按字典 §6.1「可曝光」在档位维度的投影判
            // （display 模块 discoveryReach），不内联档位串比较。`b` 是 Partial：书行未命中时按
            // 最保守档兜底，避免把取不到的行画成「正常曝光」。
            const reach = discoveryReach(b.visibility ?? 'UNLOCKED_ONLY');
            return (
              <tr key={r.id}>
                <td className="mono muted">{String(r.sort_order).padStart(2, '0')}</td>
                <td>
                  <div className="mono" style={{ fontSize: 11 }}>
                    {b.displayNovelId ?? r.novel_id}
                  </div>
                  <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                    {lang.toUpperCase()} {langName(lang)}
                  </div>
                </td>
                <td
                  style={{
                    fontSize: 12,
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <button
                    type="button"
                    aria-label={`打开书架推荐：${title}`}
                    onClick={() => onEdit(r, recs.length)}
                    onKeyDown={(event) => {
                      if (!event.repeat && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        onEdit(r, recs.length);
                      }
                    }}
                    style={{
                      padding: 0,
                      border: 0,
                      background: 'transparent',
                      color: 'inherit',
                      font: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    {title}
                  </button>
                </td>
                <td style={{ fontSize: 11.5, color: text ? 'var(--fg)' : 'var(--fg-faint)' }}>
                  {text || '未填写'}
                </td>
                <td>
                  {reach === 'NONE' ? (
                    <span
                      className="chip chip-warn"
                      style={{ fontSize: 10 }}
                      title="该书行不进发现面 · 客户端读时过滤，推荐行不自动删"
                    >
                      {DISCOVERY_REACH_LABEL.NONE} · 接口不返回
                    </span>
                  ) : reach === 'PAYING' ? (
                    <span
                      className="chip chip-info"
                      style={{ fontSize: 10 }}
                      title="该书行只对本 App 有过成功支付的用户曝光 · 其余用户读时被过滤"
                    >
                      {DISCOVERY_REACH_LABEL.PAYING}
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>—</span>
                  )}
                </td>
                <td>
                  <span
                    className={`chip ${r.is_listed ? 'chip-pos' : 'chip-warn'}`}
                    style={{ fontSize: 10 }}
                  >
                    {r.is_listed ? '上架' : '下架'}
                  </span>
                </td>
                <td>
                  <div className="hstack" style={{ gap: 2 }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-icon"
                      style={{ width: 22, height: 22 }}
                      disabled={i === 0 || !canUpsert}
                      title="上移"
                      onClick={() => move(r.id, -1)}
                    >
                      <Icons.IconChevUp size={11} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-icon"
                      style={{ width: 22, height: 22 }}
                      disabled={i === rows.length - 1 || !canUpsert}
                      title="下移"
                      onClick={() => move(r.id, 1)}
                    >
                      <Icons.IconChevDown size={11} />
                    </button>
                    <ContextMenu
                      items={[
                        {
                          label: '编辑推荐语',
                          icon: 'IconEdit',
                          disabled: !canUpsert,
                          title: !canUpsert ? FORBIDDEN_ROLE_TITLE : undefined,
                          onClick: () => onEdit(r, recs.length),
                        },
                        {
                          label: r.is_listed ? '下架' : '上架',
                          icon: r.is_listed ? 'IconPause' : 'IconPlay',
                          disabled: !canUpsert || mutations.update.isPending,
                          title: !canUpsert ? FORBIDDEN_ROLE_TITLE : undefined,
                          onClick: () =>
                            onConfirm({
                              title: `${r.is_listed ? '下架' : '上架'}推荐「${title}」？`,
                              message: r.is_listed
                                ? '下架后客户端书架不再展示该推荐位。'
                                : '上架后按排序立即出现在客户端书架。',
                              tone: r.is_listed ? 'warn' : 'default',
                              confirmLabel: `确认${r.is_listed ? '下架' : '上架'}`,
                              onConfirm: () => toggleListed(r),
                            }),
                        },
                        '---',
                        {
                          label: '移除该推荐行',
                          icon: 'IconTrash',
                          tone: 'danger',
                          disabled: !canDelete,
                          title: !canDelete ? FORBIDDEN_ROLE_TITLE : undefined,
                          onClick: () =>
                            onConfirm({
                              title: `移除推荐「${title}」？`,
                              message:
                                '仅移除该语言的这条推荐行，不影响书籍本体与其推荐语。删单条 ≠ 移除覆盖。',
                              tone: 'danger',
                              confirmLabel: '确认移除',
                              onConfirm: () =>
                                mutations.remove.mutate(r.id, {
                                  onError: (err) => errorToast(err, '删除推荐失败'),
                                }),
                            }),
                        },
                      ]}
                    >
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        style={{ width: 22, height: 22 }}
                      >
                        <Icons.IconMoreV size={12} />
                      </button>
                    </ContextMenu>
                  </div>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={7}
                style={{
                  padding: '26px 12px',
                  textAlign: 'center',
                  color: 'var(--fg-faint)',
                  fontSize: 11.5,
                }}
              >
                {emptyHint}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 10, lineHeight: 1.6 }}>
        候选只出 <span className="mono">{lang}</span> 语言书行；添加 /
        编辑时可预览并修改这本书当前语言的推荐语，改动写回书籍本体，不另存书架专用文案。
        {appName ? ` · ${appName}` : ''}
      </div>
    </Section>
  );
}
